import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Mailbox, ServerEvent } from '@mailpilot/shared';
import { absoluteUrl, authToken, type ResOf } from './api';
import { qk } from './keys';
import { bridge } from './desktop';
import { emitNotification } from './notificationBus';

type MailboxesRes = { mailboxes: Mailbox[]; providers: unknown[] };

function applyEvent(qc: QueryClient, e: ServerEvent, sessionId: string | undefined, onRevoked: () => void) {
  switch (e.type) {
    case 'mailbox.updated':
      void qc.invalidateQueries({ queryKey: qk.mailboxes });
      void qc.invalidateQueries({ queryKey: ['folders', e.mailboxId] });
      void qc.invalidateQueries({ queryKey: qk.coverage });
      break;
    case 'import.progress':
      qc.setQueryData<MailboxesRes>(qk.mailboxes, (prev) =>
        prev ? { ...prev, mailboxes: prev.mailboxes.map((m) => (m.id === e.mailboxId ? { ...m, importProgress: e.progress } : m)) } : prev,
      );
      if (e.progress.phase === 'complete') void qc.invalidateQueries({ queryKey: qk.threadsRoot });
      break;
    case 'threads.changed':
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
      void qc.invalidateQueries({ queryKey: qk.projectThreadsRoot });
      void qc.invalidateQueries({ queryKey: qk.projects });
      void qc.invalidateQueries({ queryKey: qk.categories });
      e.threadIds.forEach((id) => void qc.invalidateQueries({ queryKey: qk.thread(id) }));
      void qc.invalidateQueries({ queryKey: ['folders', e.mailboxId] });
      break;
    case 'draft.updated':
      void qc.invalidateQueries({ queryKey: qk.draftsRoot });
      if (e.threadId) void qc.invalidateQueries({ queryKey: qk.thread(e.threadId) });
      void qc.invalidateQueries({ queryKey: qk.attachmentRequestsRoot });
      break;
    case 'analysis.updated':
      void qc.invalidateQueries({ queryKey: qk.thread(e.threadId) });
      break;
    case 'attention.changed':
      void qc.invalidateQueries({ queryKey: qk.attentionRoot });
      void qc.invalidateQueries({ queryKey: qk.ringStatus });
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
      break;
    case 'review.created':
      void qc.invalidateQueries({ queryKey: qk.reviewRoot });
      break;
    case 'settings.changed':
      void qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: qk.categories });
      break;
    case 'session.revoked':
      void qc.invalidateQueries({ queryKey: qk.sessions });
      if (sessionId && e.sessionId === sessionId) onRevoked();
      break;
    case 'attachment.updated':
      void qc.invalidateQueries({ queryKey: ['thread'] });
      void qc.invalidateQueries({ queryKey: qk.attachmentRequestsRoot });
      break;
    case 'thread.presence':
      qc.setQueryData<ResOf<'GET /api/threads/:id'>>(qk.thread(e.threadId), (prev) => (prev?.team ? { ...prev, team: { ...prev.team, replying: e.replying } } : prev));
      break;
    case 'notify':
      emitNotification(e.notification);
      if (e.notification.projectId) {
        void qc.invalidateQueries({ queryKey: qk.projects });
        void qc.invalidateQueries({ queryKey: qk.projectThreadsRoot });
      }
      void qc.invalidateQueries({ queryKey: qk.notifications });
      break;
    case 'workspace.changed':
      // Membership, roles or division access changed: anything visible may have changed.
      void qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: qk.workspace });
      void qc.invalidateQueries({ queryKey: qk.mailboxes });
      void qc.invalidateQueries({ queryKey: ['folders'] });
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
      void qc.invalidateQueries({ queryKey: qk.attentionRoot });
      void qc.invalidateQueries({ queryKey: qk.reviewRoot });
      break;
  }
}

/**
 * Subscribe to /api/events (SSE) and turn ServerEvents into cache invalidations. Reconnects with backoff,
 * and whenever the workspace changes (the server binds a stream to the workspace it was opened in).
 */
export function useRealtime(enabled: boolean, sessionId: string | undefined, workspaceId: string | undefined, onRevoked: () => void) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let es: EventSource | null = null;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const connect = async () => {
      if (disposed) return;
      let url = await absoluteUrl('/api/events');
      if (bridge()) {
        const t = await authToken();
        if (t) url += `?token=${encodeURIComponent(t)}`;
      }
      es = new EventSource(url, { withCredentials: true });
      es.addEventListener('ready', () => {
        if (attempt > 0) {
          // we may have missed events while disconnected
          void qc.invalidateQueries();
        }
        attempt = 0;
      });
      es.onmessage = (m) => {
        try {
          applyEvent(qc, JSON.parse(m.data as string) as ServerEvent, sessionId, onRevoked);
        } catch {
          /* ignore malformed */
        }
      };
      es.onerror = () => {
        es?.close();
        es = null;
        if (disposed) return;
        attempt += 1;
        const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)) + Math.random() * 500;
        timer = setTimeout(() => void connect(), delay);
      };
    };
    void connect();
    const onOnline = () => {
      if (!es) {
        if (timer) clearTimeout(timer);
        attempt = 1;
        void connect();
      }
    };
    window.addEventListener('online', onOnline);
    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      es?.close();
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, sessionId, workspaceId, qc, onRevoked]);
}
